import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/lib/auth-context';
import { attendanceAPI, branchAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatusPill } from '@/components/StatusPill';
import { toast } from 'sonner';
import { Clock, MapPin, Fingerprint, Wifi, CheckCircle, AlertTriangle, LogIn, LogOut, CalendarClock } from 'lucide-react';

export default function AttendanceCheckinPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [todaySchedule, setTodaySchedule] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState({ fingerprint: null, gps: null, ip: null });
  const [gpsStatus, setGpsStatus] = useState('loading');
  const fpRef = useRef(null);

  // Update clock every second
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Load fingerprint
  useEffect(() => {
    const loadFingerprint = async () => {
      try {
        const FingerprintJS = (await import('@fingerprintjs/fingerprintjs')).default;
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        fpRef.current = result.visitorId;
        setDeviceInfo(prev => ({ ...prev, fingerprint: result.visitorId }));
      } catch (err) {
        console.error('Fingerprint error:', err);
        fpRef.current = 'fp-' + Math.random().toString(36).substr(2, 9);
        setDeviceInfo(prev => ({ ...prev, fingerprint: fpRef.current }));
      }
    };
    loadFingerprint();
  }, []);

  // Load today's schedule
  useEffect(() => {
    if (user?.branch_id) {
      branchAPI.todaySchedule(user.branch_id).then(res => {
        setTodaySchedule(res.data);
      }).catch((e) => console.error(e));
    }
  }, [user?.branch_id]);


  // Load GPS
  const requestGps = useCallback(() => {
    if (navigator.geolocation) {
      setGpsStatus('requesting');
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setDeviceInfo(prev => ({
            ...prev,
            gps: {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy: pos.coords.accuracy
            }
          }));
          setGpsStatus('granted');
        },
        (err) => {
          console.warn('GPS denied:', err.message);
          setGpsStatus('denied');
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setGpsStatus('unavailable');
    }
  }, []);

  useEffect(() => { requestGps(); }, [requestGps]);

  // Load attendance status
  const loadStatus = useCallback(async () => {
    try {
      const res = await attendanceAPI.myStatus();
      setStatus(res.data);
    } catch (err) {
      console.error('Status error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleCheckIn = async () => {
    setChecking(true);
    try {
      const payload = {
        fingerprint_id: fpRef.current || 'unknown',
        gps_latitude: deviceInfo.gps?.latitude || null,
        gps_longitude: deviceInfo.gps?.longitude || null,
        gps_accuracy: deviceInfo.gps?.accuracy || null,
        gps_permission_state: gpsStatus,
        user_agent: navigator.userAgent
      };
      const res = await attendanceAPI.checkIn(payload);
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        toast.success('Checked in successfully!');
        if (res.data.late_message) toast.warning(res.data.late_message, { duration: 6000 });
        await loadStatus();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-in failed');
    } finally {
      setChecking(false);
    }
  };

  const handleCheckOut = async () => {
    if (!status?.attendance?.id) return;
    setChecking(true);
    try {
      await attendanceAPI.checkOut({ attendance_id: status.attendance.id });
      toast.success('Checked out successfully!');
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-out failed');
    } finally {
      setChecking(false);
    }
  };

  const isCheckedIn = status?.checked_in;
  const attendance = status?.attendance;
  const branch = status?.branch;

  // Format time for branch timezone
  const formatBranchTime = () => {
    if (branch?.timezone) {
      try {
        return currentTime.toLocaleTimeString('en-US', {
          timeZone: branch.timezone,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
      } catch {
        return currentTime.toLocaleTimeString();
      }
    }
    return currentTime.toLocaleTimeString();
  };

  const formatBranchDate = () => {
    if (branch?.timezone) {
      try {
        return currentTime.toLocaleDateString('en-US', {
          timeZone: branch.timezone,
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch {
        return currentTime.toLocaleDateString();
      }
    }
    return currentTime.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse space-y-4 text-center">
          <div className="h-16 w-16 rounded-full bg-muted mx-auto" />
          <div className="h-4 w-32 bg-muted rounded mx-auto" />
        </div>
      </div>
    );
  }

  // Contractors don't need to check in
  if (user?.employment_type === 'contractor') {
    return (
      <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
        <Card className="overflow-hidden">
          <CardContent className="py-12 text-center">
            <div className="h-16 w-16 rounded-full bg-muted mx-auto flex items-center justify-center mb-4">
              <CheckCircle className="text-primary" size={32} />
            </div>
            <h2 className="text-xl font-heading font-semibold mb-2">Contractor Account</h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              As a contractor, you are not required to check in or check out. Your attendance is managed separately by your supervisor.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      {/* GPS prompt */}
      {gpsStatus !== 'granted' && (
        <div className="flex items-start gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900" data-testid="gps-prompt">
          <MapPin size={18} className="shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <p className="font-medium">Please turn on your location (GPS)</p>
            <p className="text-amber-800/80 text-xs mt-0.5">
              Check in from the office on your company laptop. Enable location so we can verify your check-in.
              {gpsStatus === 'denied' && ' Location permission was denied — please allow it in your browser settings, then retry.'}
              {gpsStatus === 'unavailable' && ' Location is not available on this device/browser.'}
            </p>
          </div>
          <Button size="sm" variant="outline" className="h-8 border-amber-300" onClick={requestGps} data-testid="gps-retry">
            Enable GPS
          </Button>
        </div>
      )}

      {/* Today's Schedule Banner */}
      {todaySchedule && (
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-card" data-testid="today-schedule-banner">
          <CalendarClock size={18} className="text-primary shrink-0" />
          <div className="flex-1 text-sm">
            <span className="font-medium">{todaySchedule.day_name}</span>
            {todaySchedule.is_working ? (
              <span className="text-muted-foreground ml-2">
                Office hours: {todaySchedule.start_time} – {todaySchedule.end_time}
              </span>
            ) : (
              <Badge variant="outline" className="ml-2 text-xs">Day Off</Badge>
            )}
          </div>
          <span className="text-xs text-muted-foreground tabular-nums">{todaySchedule.timezone}</span>
        </div>
      )}

      {/* Status Card */}
      <Card className="overflow-hidden">
        <div className="bg-primary/5 border-b px-6 py-8 text-center">
          <p className="text-sm text-muted-foreground mb-1">{formatBranchDate()}</p>
          <p className="text-5xl md:text-6xl font-heading font-bold tabular-nums tracking-tight">
            {formatBranchTime()}
          </p>
          {branch && (
            <p className="text-sm text-muted-foreground mt-2">
              {branch.name} &middot; Office starts at {branch.office_start_time}
              {branch.grace_minutes && ` (${branch.grace_minutes} min grace)`}
            </p>
          )}
        </div>

        <CardContent className="p-6">
          {/* Main CTA */}
          <div className="text-center space-y-4">
            {!attendance ? (
              <Button
                size="lg"
                className="h-16 w-full text-lg gap-3"
                onClick={handleCheckIn}
                disabled={checking}
                data-testid="attendance-checkin-button"
              >
                <LogIn size={24} />
                {checking ? 'Checking in...' : 'Check In'}
              </Button>
            ) : isCheckedIn ? (
              <div className="space-y-3">
                <div className="flex items-center justify-center gap-2 text-green-700">
                  <CheckCircle size={20} />
                  <span className="font-medium">Checked In</span>
                  {attendance.is_late && (
                    <StatusPill status={attendance.is_half_day ? 'half-day' : 'late'}>
                      {attendance.late_minutes} min late
                    </StatusPill>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  Check-in time: <span className="font-medium tabular-nums">{attendance.check_in_display || attendance.check_in_time_local}</span>
                </p>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 w-full text-lg gap-3 border-2"
                  onClick={handleCheckOut}
                  disabled={checking}
                  data-testid="attendance-checkout-button"
                >
                  <LogOut size={20} />
                  {checking ? 'Checking out...' : 'Check Out'}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-center gap-2 text-muted-foreground">
                  <CheckCircle size={20} />
                  <span className="font-medium">Day Complete</span>
                  <StatusPill status="checked-out" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Checked in: <span className="tabular-nums">{attendance.check_in_display || attendance.check_in_time_local}</span>
                  {attendance.check_out_time && (
                    <> &middot; Checked out: <span className="tabular-nums">{attendance.check_out_display || new Date(attendance.check_out_time).toLocaleTimeString()}</span></>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Monthly summary */}
          {status && (
            <div className="mt-6 pt-6 border-t grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-heading font-semibold tabular-nums text-amber-600">{status.monthly_lates || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Lates this month</p>
              </div>
              <div>
                <p className="text-2xl font-heading font-semibold tabular-nums text-blue-600">{status.monthly_half_days || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Half days</p>
              </div>
              <div>
                <p className="text-2xl font-heading font-semibold tabular-nums text-green-600">
                  {status.local_time ? new Date().getDate() - (status.monthly_lates || 0) - (status.monthly_half_days || 0) : '-'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">On-time days</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Device Trust Panel */}
      <Card data-testid="attendance-device-info-panel">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground">Device Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <Fingerprint size={16} className="text-muted-foreground shrink-0" />
            <span className="text-muted-foreground">Device ID:</span>
            <span className="font-mono text-xs truncate">{deviceInfo.fingerprint || 'Loading...'}</span>
          </div>
          <div className="flex items-center gap-3 text-sm" data-testid="attendance-gps-status">
            <MapPin size={16} className={gpsStatus === 'granted' ? 'text-green-600' : 'text-amber-500'} />
            <span className="text-muted-foreground">GPS:</span>
            {gpsStatus === 'granted' ? (
              <span className="text-green-700">Active ({deviceInfo.gps?.accuracy?.toFixed(0)}m accuracy)</span>
            ) : gpsStatus === 'denied' ? (
              <span className="text-amber-600">Permission denied - attendance will still be recorded</span>
            ) : (
              <span className="text-muted-foreground">Requesting...</span>
            )}
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Wifi size={16} className="text-muted-foreground shrink-0" />
            <span className="text-muted-foreground">IP:</span>
            <span>{attendance?.ip_address || 'Will be captured on check-in'}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
